import type { ChatMessageUsage } from '@/features/global-ai-chat/api/types';

/** i18n 言語キーと Intl が解釈できる BCP 47 タグの差分を吸収する。 */
const INTL_LOCALE_ALIASES: Record<string, string> = {
  'vn-VN': 'vi-VN',
  zhCN: 'zh-CN',
};

function normalizeIntlLocale(locale: string): string {
  const aliased = INTL_LOCALE_ALIASES[locale] ?? locale;
  return aliased.replace(/^([a-z]{2})([A-Z]{2})$/, '$1-$2');
}

/** 画面表示用の数値フォーマット（ロケールに合わせた桁区切り）。 */
export function formatTokenCount(value: number, locale: string): string {
  try {
    return value.toLocaleString(normalizeIntlLocale(locale));
  } catch {
    return value.toLocaleString();
  }
}

/** Guardrails 概算 USD（ai-operator と同じく小数第 6 位まで、丸めない）。 */
export function formatGuardrailUsdEstimate(usd: number): string {
  return `$${usd.toFixed(6)}`;
}

export type ChatMessageUsageDisplay = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  guardrailEstimatedUsd?: number;
};

/** 画面表示用に usage を整形する。 */
export function toChatMessageUsageDisplay(
  usage: ChatMessageUsage,
): ChatMessageUsageDisplay {
  return {
    inputTokens: usage.inputTokens,
    outputTokens: usage.outputTokens,
    totalTokens: usage.inputTokens + usage.outputTokens,
    guardrailEstimatedUsd: usage.guardrailEstimatedUsd,
  };
}

/** eval / CLI 向けの技術 1 行（開発者用。UI では使わない）。 */
export function formatChatMessageUsageLine(usage: ChatMessageUsage): string {
  const total = usage.inputTokens + usage.outputTokens;
  const n = (v: number) => v.toLocaleString('en-US');
  let line = `FM  input: ${n(usage.inputTokens)}. output: ${n(usage.outputTokens)}. total: ${n(total)}.`;
  if (
    usage.guardrailTextUnits !== undefined &&
    usage.guardrailEstimatedUsd !== undefined
  ) {
    line += ` | GR ~${usage.guardrailTextUnits} units ~$${usage.guardrailEstimatedUsd.toFixed(6)}`;
  }
  return line;
}
